import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import './styles.css';

// Lazy load pages for better performance
const About = lazy(() => import('./pages/About'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const Now = lazy(() => import('./pages/Now'));
const Archive = lazy(() => import('./pages/Archive'));
const Projects = lazy(() => import('./pages/Projects'));
const Newsletter = lazy(() => import('./pages/Newsletter'));
const ReadingListPage = lazy(() => import('./pages/ReadingListPage'));
const PillarPage = lazy(() => import('./pages/PillarPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader" style={{ padding: '2rem', textAlign: 'center' }}>
    Loading...
  </div>
);

const App = () => {
  const [darkMode, setDarkMode] = useState(false);

  // Initialize dark mode from localStorage or system preference
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      setDarkMode(JSON.parse(savedMode));
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(prefersDark);
    }
  }, []);

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <Router>
      <Helmet>
        <title>Bjarne Christiansen</title>
        <meta
          name="description"
          content="Bjarne Christiansen — co-founder of Realm (YC S11, acquired by MongoDB, 2B+ installs). Writes on startup fundamentals, fundraising from both sides of the table, and the Nordic founder advantage."
        />
        <meta
          name="keywords"
          content="Bjarne Christiansen, startup fundamentals, fundraising, Nordic ecosystem, Realm, MongoDB, Y Combinator, capital efficiency, founder"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://christiansen.co/" />
        <meta
          property="og:title"
          content="Bjarne Christiansen | Startup Fundamentals, Fundraising, Nordic Edge"
        />
        <meta
          property="og:description"
          content="Co-founded Realm (YC S11, acquired by MongoDB). Raised $40M. Writes about startup fundamentals, fundraising from both sides of the table, and the Nordic founder advantage."
        />
        <meta property="og:site_name" content="Bjarne Christiansen" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bjchrist" />
        <meta name="twitter:creator" content="@bjchrist" />
        <link rel="canonical" href="https://christiansen.co/" />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Bjarne Christiansen's Writing"
          href="https://christiansen.co/rss.xml"
        />
      </Helmet>
      <div className="app-container">
        <a href="#main-content" className="skip-to-content">
          Skip to main content
        </a>
        <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <main id="main-content" className="content">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/now" element={<Now />} />
                <Route path="/archive" element={<Archive />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/reading-list" element={<ReadingListPage />} />
                <Route path="/startup-fundamentals" element={<PillarPage pillar="startup-fundamentals" />} />
                <Route path="/fundraising" element={<PillarPage pillar="fundraising" />} />
                <Route path="/nordic" element={<PillarPage pillar="nordic" />} />
                {/* Redirects for removed pages */}
                <Route path="/experience" element={<Navigate to="/about" replace />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/speaking" element={<Navigate to="/about" replace />} />
                <Route path="/hire-me" element={<Navigate to="/about" replace />} />
                <Route path="/contact" element={<Navigate to="/about" replace />} />
                <Route
                  path="/what-is-a-fractional-cto"
                  element={<Navigate to="/about" replace />}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        {/* ARIA live region for dynamic announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only" id="announcements"></div>
      </div>
    </Router>
  );
};

export default App;
